import { useMemo, useState } from "react";
import { useGetIdentity, useGetList, useTranslate } from "ra-core";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useIsMobile } from "@/hooks/use-mobile";

import {
  ListEmptyState,
  ListErrorState,
  ListRowsSkeleton,
} from "../design/ListStates";
import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { MobileBackButton } from "../misc/MobileBackButton";
import { BUSINESS_ARM_LABELS } from "../sales/businessArms";
import type { OrgChartPerson } from "../types";
import { OrgChartPersonCard } from "./OrgChartPersonCard";
import {
  BPO_UNASSIGNED_PROJECT_LABEL,
  ORG_CHART_ARM_LABELS,
  buildOrgChartGroups,
  getCardRelationships,
  getPositionLabel,
  indexOrgChartPeopleById,
  type OrgChartGroup,
} from "./orgChartLayout";

// Generous fixed cap (mirrors `GoalsPage.tsx`'s `MAX_AUDIENCE_SALES`
// convention): a single request for the whole chart, per the ticket's own
// `performance` non-functional requirement.
const MAX_PEOPLE = 500;

const OrgChartPersonRow = ({
  person,
  onSelect,
}: {
  person: OrgChartPerson;
  onSelect: (person: OrgChartPerson) => void;
}) => {
  const positionLabel = getPositionLabel(person);
  const armLabel = person.business_arm
    ? BUSINESS_ARM_LABELS[person.business_arm]
    : undefined;
  const projectNames = person.projects
    .map((project) => project.name)
    .join(", ");

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(person)}
        className="flex min-h-11 w-full items-center gap-3 rounded-2xl bg-card p-3 text-left shadow-sm focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-brand-yellow"
      >
        <Avatar className="size-10 shrink-0">
          <AvatarImage src={person.avatar?.src ?? undefined} />
          <AvatarFallback>
            {person.first_name.charAt(0).toUpperCase()}
            {person.last_name.charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-base font-semibold">
            {person.first_name} {person.last_name}
          </span>
          <span className="truncate text-sm text-muted-foreground">
            {armLabel ? `${positionLabel} · ${armLabel}` : positionLabel}
          </span>
          {projectNames ? (
            <span className="truncate text-sm text-muted-foreground">
              {projectNames}
            </span>
          ) : null}
        </div>
      </button>
    </li>
  );
};

const OrgChartGroupSection = ({
  group,
  onSelect,
}: {
  group: OrgChartGroup;
  onSelect: (person: OrgChartPerson) => void;
}) => (
  <section className="flex flex-col gap-3">
    <h2 className="text-xl font-semibold text-muted-foreground">
      {ORG_CHART_ARM_LABELS[group.key]}
    </h2>
    <div className="flex flex-col gap-4">
      {group.subgroups.map((subgroup) => (
        <div key={String(subgroup.key)} className="flex flex-col gap-2">
          {subgroup.projectName ? (
            <h3 className="text-base font-semibold">{subgroup.projectName}</h3>
          ) : subgroup.key === "sin_proyecto" ? (
            <h3 className="text-base font-semibold text-muted-foreground">
              {BPO_UNASSIGNED_PROJECT_LABEL}
            </h3>
          ) : null}
          <ul className="flex flex-col gap-2">
            {subgroup.people.map((person) => (
              <OrgChartPersonRow
                key={person.id}
                person={person}
                onSelect={onSelect}
              />
            ))}
          </ul>
        </div>
      ))}
    </div>
  </section>
);

/**
 * Almikia Epic EO (TASK-098): the "Organigrama" screen -- reads ONLY the
 * live `org_chart_people` view (never `sales`, which carries every clave),
 * grouped and ordered per DOMAIN RULES EO (c): brazo, then (inside BPO) one
 * subgroup per active project, then position, then name -- never by any
 * performance measure. Tapping a person opens {@link OrgChartPersonCard}.
 * Live: no stored snapshot, no manual rebuild -- a fresh `getList` on every
 * visit/refresh is the whole "live" implementation.
 */
export const OrgChartPage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { isPending: isIdentityPending } = useGetIdentity();

  const { data, isPending, error, refetch } = useGetList<OrgChartPerson>(
    "org_chart_people",
    {
      pagination: { page: 1, perPage: MAX_PEOPLE },
      sort: { field: "first_name", order: "ASC" },
    },
  );

  const people = useMemo(() => data ?? [], [data]);
  const peopleById = useMemo(() => indexOrgChartPeopleById(people), [people]);
  const groups = useMemo(() => buildOrgChartGroups(people), [people]);

  const [selected, setSelected] = useState<OrgChartPerson | null>(null);
  const relationships = selected
    ? getCardRelationships(selected, peopleById)
    : [];

  const loading = isIdentityPending || isPending;

  const body = loading ? (
    <ListRowsSkeleton rows={6} columns={2} />
  ) : error ? (
    <ListErrorState
      message={translate("crm.common.list_error")}
      onRetry={refetch}
    />
  ) : groups.length === 0 ? (
    <ListEmptyState
      title={translate("crm.org_chart.empty_title")}
      description={translate("crm.org_chart.empty_description")}
    />
  ) : (
    <div className="flex flex-col gap-6">
      {groups.map((group) => (
        <OrgChartGroupSection
          key={group.key}
          group={group}
          onSelect={setSelected}
        />
      ))}
    </div>
  );

  const card = (
    <OrgChartPersonCard
      person={selected}
      relationships={relationships}
      open={selected != null}
      onOpenChange={(open) => {
        if (!open) setSelected(null);
      }}
    />
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <MobileBackButton to="/almikia" />
          <div className="flex flex-1 min-w-0">
            <h1 className="text-xl font-semibold truncate">
              {translate("crm.org_chart.title")}
            </h1>
          </div>
        </MobileHeader>
        <MobileContent>{body}</MobileContent>
        {card}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <h1 className="text-2xl font-bold">{translate("crm.org_chart.title")}</h1>
      {body}
      {card}
    </div>
  );
};

OrgChartPage.path = "/almikia/organigrama";
